import { useState } from "react";
import Navbar from "../home/components/Navbar";
import Footer from "../home/components/Footer";
import QuoteModal from "../home/components/QuoteModal";
import AccountSidebar from "./components/AccountSidebar";
import ProfileTab from "./components/ProfileTab";
import BookingsTab from "./components/BookingsTab";
import AddressesTab from "./components/AddressesTab";
import PaymentsTab from "./components/PaymentsTab";
import { useAuth } from "../../context/AuthContext";

type Tab = "profile" | "bookings" | "addresses" | "payments";

export default function AccountPage() {
  const [activeTab, setActiveTab] = useState<Tab>("bookings");
  const [quoteOpen, setQuoteOpen] = useState(false);
  const { user, loading: authLoading, login, logout } = useAuth();

  // Simple inline login form when not authenticated
  if (!authLoading && !user) {
    return (
      <div className="min-h-screen flex flex-col bg-cl-tint">
        <Navbar onBookNow={() => setQuoteOpen(true)} />
        <main className="flex-1 flex items-center justify-center py-16 px-6">
          <LoginCard onLogin={login} />
        </main>
        <Footer />
        {quoteOpen && <QuoteModal onClose={() => setQuoteOpen(false)} />}
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-cl-tint">
      <Navbar onBookNow={() => setQuoteOpen(true)} />
      <main className="flex-1">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 py-12">
          <div className="flex flex-col lg:flex-row gap-8">
            <AccountSidebar
              activeTab={activeTab}
              onTabChange={setActiveTab as (t: string) => void}
              user={user}
              onLogout={logout}
            />
            <div className="flex-1 min-w-0">
              {activeTab === "profile"   && <ProfileTab user={user} />}
              {activeTab === "bookings"  && <BookingsTab />}
              {activeTab === "addresses" && <AddressesTab user={user} />}
              {activeTab === "payments"  && <PaymentsTab />}
            </div>
          </div>
        </div>
      </main>
      <Footer />
      {quoteOpen && <QuoteModal onClose={() => setQuoteOpen(false)} />}
    </div>
  );
}

// ── Inline login form ─────────────────────────────────────────────────────────
function LoginCard({ onLogin }: { onLogin: (u: string, p: string) => Promise<void> }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError]       = useState("");
  const [loading, setLoading]   = useState(false);

  const submit = async () => {
    setError("");
    setLoading(true);
    try {
      await onLogin(username, password);
    } catch (e) {
      setError((e as Error)?.message ?? "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-3xl cartoon-card p-10 w-full max-w-md">
      <h1 className="text-3xl font-black text-cl-dark mb-2">My Account</h1>
      <p className="text-gray-500 text-sm font-semibold mb-8">Sign in to view your bookings and manage your account.</p>
      {error && (
        <p className="mb-4 p-3 bg-red-50 border-2 border-red-200 rounded-xl text-red-700 text-sm font-semibold">{error}</p>
      )}
      <div className="space-y-4 mb-6">
        <div>
          <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-1.5">Email</label>
          <input
            type="email"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="your@email.co.uk"
            className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-sm font-semibold focus:outline-none focus:border-cl-green"
          />
        </div>
        <div>
          <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-1.5">Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-sm font-semibold focus:outline-none focus:border-cl-green"
            onKeyDown={(e) => e.key === "Enter" && submit()}
          />
        </div>
      </div>
      <button
        onClick={submit}
        disabled={loading}
        className="w-full bg-cl-green text-white font-black py-4 rounded-xl text-sm cartoon-btn-green disabled:opacity-60"
      >
        {loading ? "Signing in…" : "Sign In"}
      </button>
      <p className="text-center text-xs text-gray-400 font-semibold mt-5">
        Don't have an account? <a href="#" className="text-cl-green font-black">Register here</a>
      </p>
    </div>
  );
}
